import { SupportDay, SupportHoursDTO } from 'models/conversations'

const ISO_WEEKDAY: Record<SupportDay, number> = {
  [SupportDay.MONDAY]: 1,
  [SupportDay.TUESDAY]: 2,
  [SupportDay.WEDNESDAY]: 3,
  [SupportDay.THURSDAY]: 4,
  [SupportDay.FRIDAY]: 5,
  [SupportDay.SATURDAY]: 6,
  [SupportDay.SUNDAY]: 7,
}

const REFERENCE_MONDAY_UTC = Date.UTC(2024, 0, 1, 12)
const DAY_MS = 86_400_000
const MIN_DAYS_FOR_RANGE = 3

const isSupportDay = (day: string): day is SupportDay => Object.prototype.hasOwnProperty.call(ISO_WEEKDAY, day)

const weekdayLabel = (day: SupportDay, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
    .format(new Date(REFERENCE_MONDAY_UTC + (ISO_WEEKDAY[day] - 1) * DAY_MS))

export const formatSupportDays = (days: SupportDay[], locale: string): string => {
  const sorted = [...new Set(days)].filter(isSupportDay).sort((a, b) => ISO_WEEKDAY[a] - ISO_WEEKDAY[b])
  if (!sorted.length) return ''
  const isConsecutive = sorted.every((day, index) => index === 0 || ISO_WEEKDAY[day] === ISO_WEEKDAY[sorted[index - 1]] + 1)
  if (isConsecutive && sorted.length >= MIN_DAYS_FOR_RANGE) {
    return `${weekdayLabel(sorted[0], locale)}–${weekdayLabel(sorted[sorted.length - 1], locale)}`
  }
  return sorted.map((day) => weekdayLabel(day, locale)).join(', ')
}

export const supportTimezoneCity = (timezone: string): string =>
  (timezone.split('/').pop() || timezone).replace(/_/g, ' ')

export const formatReopensAt = (iso: string, locale: string, timeZone?: string): string | null => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(date)
}

export const isSupportClosedAt = (hours: SupportHoursDTO | undefined, now: number): boolean => {
  if (!hours || hours.openNow) return false
  const nextOpening = Date.parse(hours.nextOpeningAt)
  return Number.isNaN(nextOpening) || now < nextOpening
}

export const hasOpenedSince = (hours: SupportHoursDTO, since: string): boolean =>
  hours.openNow || !hours.closedSince || new Date(since).getTime() < new Date(hours.closedSince).getTime()

export type OutsideHoursFallbackReason = 'no_hours' | 'open_again' | 'reopened_since' | 'unreadable'

export const outsideHoursFallbackReason = (
  hours: SupportHoursDTO | undefined,
  noticeCreatedAt: string,
  now: number
): OutsideHoursFallbackReason | null => {
  if (!hours) return 'no_hours'
  if (!isSupportClosedAt(hours, now)) return 'open_again'
  if (hasOpenedSince(hours, noticeCreatedAt)) return 'reopened_since'
  if (Number.isNaN(Date.parse(hours.nextOpeningAt))) return 'unreadable'
  return null
}

export const reopensAtForNotice = (
  hours: SupportHoursDTO | undefined,
  noticeCreatedAt: string,
  locale: string,
  now: number,
  timeZone?: string
): string | null => {
  if (!hours || outsideHoursFallbackReason(hours, noticeCreatedAt, now)) return null
  return formatReopensAt(hours.nextOpeningAt, locale, timeZone)
}
